import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pure pipe for processing card poster URLs
 * Converts empty/null/undefined URLs to mock image and encodes valid URLs
 *
 * Using a pure pipe ensures Angular only recalculates when input changes,
 * preventing performance issues during mass rendering
 */
@Pipe({
  name: 'posterUrl',
  pure: true // Important: pure pipe for performance
})
export class PosterUrlPipe implements PipeTransform {
  private static readonly MOCK_IMAGE = '/assets/images/gradientMock.png';

  // Cache for encodeURI results to avoid redundant calls
  private static urlCache = new Map<string, string>();
  private static cacheSize = 0;
  private static readonly MAX_CACHE_SIZE = 500; // Limit cache size to prevent memory leaks

  transform(cardPosterUrl: string | null | undefined, cardId?: string): string {
    // Fast path: empty/null/undefined
    if (!cardPosterUrl || cardPosterUrl === '') {
      // Logging disabled to prevent console overload - use debug overlay instead
      return PosterUrlPipe.MOCK_IMAGE;
    }

    // Check cache first
    const cached = PosterUrlPipe.urlCache.get(cardPosterUrl);
    if (cached !== undefined) {
      return cached;
    }

    // Encode and cache
    try {
      const encoded = encodeURI(cardPosterUrl);

      // Limit cache size to prevent memory leaks
      if (PosterUrlPipe.cacheSize >= PosterUrlPipe.MAX_CACHE_SIZE) {
        // Clear oldest entries (simple FIFO)
        const firstKey = PosterUrlPipe.urlCache.keys().next().value;
        PosterUrlPipe.urlCache.delete(firstKey);
        PosterUrlPipe.cacheSize--;
      }

      PosterUrlPipe.urlCache.set(cardPosterUrl, encoded);
      PosterUrlPipe.cacheSize++;

      return encoded;
    } catch (error) {
      // Only log critical errors
      console.error('[PosterUrlPipe] Error encoding URL for card:', cardId);
      return PosterUrlPipe.MOCK_IMAGE;
    }
  }

  /**
   * Clear cache (useful for testing or memory management)
   */
  static clearCache(): void {
    PosterUrlPipe.urlCache.clear();
    PosterUrlPipe.cacheSize = 0;
  }
}

